import * as React from "react";
import * as ReactDOM from "react-dom";
import * as Resources from "TestManagement/Scripts/Resources/TFS.Resources.TestManagement";
import { registerLWPComponent } from "VSS/LWP";
import { MruClassificationPicker, IMruClassificationPickerProps } from "WorkItemTracking/Scripts/MruClassificationPicker/Components/MruClassificationPicker";
import {
    IFormFieldState,
    INodeField,
    IValidationResult,
    WorkItemField
} from "TestManagement/Scripts/Scenarios/NewTestPlanHub/Contracts";
import { INode } from "Presentation/Scripts/TFS/FeatureRef/TFS.AgileCommon";
import { css } from "VSSUI/Util";
import { NewTestPlanFormValidators } from "TestManagement/Scripts/Scenarios/NewTestPlanHub/Components/NewTestPlanFormValidators";
import { TreePickerDataSource } from "VSSPreview/Controls/TreePicker";

export interface IAreaPathAndIterationPickerProps {
    areaPathNode: INode;
    iterationNode: INode;
    projectId: string;
    areaPathNodeSelected: (path: string) => void;
    iterationNodeSelected: (path: string) => void;
    selectedAreaPath?: string;
    selectedIteration?: string;
    areaPathMruItemIds: number[];
    iterationMruItemIds: number[];
    asteriskRequired: boolean;
}

export interface IAreaPathAndIterationPickerState {
    rootAreaPath: INodeField;
    selectedAreaPathField: IFormFieldState<INodeField>;
    rootIteration: INodeField;
    selectedIterationField: IFormFieldState<INodeField>;
    projectId: string;
}

// This is a proxy component to load the area path and iteration picker in the new web platform using LegacyComponent
class AreaPathAndIterationPicker extends React.Component<IAreaPathAndIterationPickerProps, IAreaPathAndIterationPickerState>{
    private _classificationPicker: MruClassificationPicker;
    private readonly _dropDownHeight: number = 24;
    private readonly _dropdownWidth: number = 10;
    private readonly _visibleItemCount: number = 10;
    constructor(props: IAreaPathAndIterationPickerProps) {
        super(props);

        this.state = this._getInitialState(props);
    }

    private _getInitialState(props: IAreaPathAndIterationPickerProps) {

        let areaPathTreePickerDataSource = new TreePickerDataSource(props.areaPathNode, "\\", props.areaPathMruItemIds);
        let iterationTreePickerDataSource = new TreePickerDataSource(props.iterationNode, "\\", props.iterationMruItemIds);

        // get the most recently used node's id.
        let selectedAreaPathId = (props.areaPathMruItemIds && props.areaPathMruItemIds.length > 0) ? props.areaPathMruItemIds[0] : props.areaPathNode.id;
        let selectedIterationId = (props.iterationMruItemIds && props.iterationMruItemIds.length > 0) ? props.iterationMruItemIds[0] : props.iterationNode.id;

        let selectedAreaPath = areaPathTreePickerDataSource.getPathById(selectedAreaPathId);
        let selectedIteration = iterationTreePickerDataSource.getPathById(selectedIterationId);
        
        // if we don't find the node, assign the root node's value
        selectedAreaPath = selectedAreaPath ? selectedAreaPath : props.areaPathNode.name; 
        selectedIteration = selectedIteration ? selectedIteration : props.iterationNode.name;

        let selectedAreaPathNode = this._findNodeInTree(selectedAreaPath, props.areaPathNode, 1);
        let selectedIterationNode = this._findNodeInTree(selectedIteration, props.iterationNode, 1);

        selectedAreaPathNode = selectedAreaPathNode ? selectedAreaPathNode : props.areaPathNode;
        selectedIterationNode = selectedIterationNode ? selectedIterationNode : props.iterationNode;

        this.props.areaPathNodeSelected(selectedAreaPath);
        this.props.iterationNodeSelected(selectedIteration);

        return {
            rootAreaPath: {
                path: props.areaPathNode.name,
                node: props.areaPathNode
            },
            selectedAreaPathField: {
                value: {
                    path: selectedAreaPath,
                    node: selectedAreaPathNode
                },
                validationResult: { isValid: (selectedAreaPathNode != null) }
            },
            rootIteration: {
                path: props.iterationNode.name,
                node: props.iterationNode
            },
            selectedIterationField: {
                value: {
                    path: selectedIteration,
                    node: selectedIterationNode
                },
                validationResult: { isValid: (selectedIterationNode != null) }
            },
            projectId: props.projectId,
        };
    }

    // This method is used to find the node object in the project's complete area/iteration tree.
    // currentDepth indicates the position of the node in the full path.
    // For example for a node path a\b\c: a (root) has current depth as 1, b - 2 and c - 3.
    private _findNodeInTree(nodeName: string, rootNode: INode, currentDepth: number): INode {
        if (nodeName) {
            let splitPath = this._getSplitPath(nodeName);

            if (nodeName.endsWith(rootNode.name) && currentDepth === splitPath.length) {
                return rootNode;
            }
    
            // Performing a DFS on the tree.
            if (rootNode.children) {
                for (var i = 0; i < rootNode.children.length; i++) {
                    // Verifying that the current node being traversered is part of the full path.
                    if (splitPath[currentDepth - 1] === rootNode.name) {
                        var node = this._findNodeInTree(nodeName, rootNode.children[i], currentDepth + 1);

                        if (node) {
                            return node;
                        }
                    }
                }
            }
        }

        return null;
    }

    private _getSplitPath(nodeName: string): string[] {
        if (nodeName) {
            return nodeName.split("\\");
        }

        return [""];
    }

    public render(): React.ReactNode {

        const {
            rootAreaPath,
            selectedAreaPathField,
            rootIteration,
            selectedIterationField
        } = this.state;

        return (<>
            <div className="test-plan-area-path" key="area-path">
                {this._renderNodeSection(rootAreaPath,
                    selectedAreaPathField,
                    WorkItemField.areaPath,
                    Resources.AreaPath,
                    this._onAreaPathNodeChanged)}
            </div>
            <div className="test-plan-iteration" key="iteration">
                {this._renderNodeSection(rootIteration,
                    selectedIterationField,
                    WorkItemField.iterationPath,
                    Resources.Iteration,
                    this._onIterationNodeChanged)}
            </div>
        </>);
    }

    private _renderNodeSection(root: INodeField,
        selectedField: IFormFieldState<INodeField>,
        refName: string,
        label: string,
        onNodeChanged: any): JSX.Element {
        const classNames = css("workitem-classification-picker", { "invalid": false }, { "readonly": true });
        return <MruClassificationPicker
            aria-label={label}
            aria-required={true}
            labelProps={{required: this.props.asteriskRequired}}
            label={label}
            errorMessage={selectedField.changed && !selectedField.validationResult.isValid ? selectedField.validationResult.errorMessage : ""}
            componentRef={this._resolveClassificationPicker}
            containerClassName={classNames}
            calloutClassName="workitem-classification-picker-dropdown"
            openCalloutOnFocus={false}
            dropdownItemHeight={this._dropDownHeight}
            dropdownItemIndentWidth={this._dropdownWidth}
            fieldRefName={refName}
            projectId={this.state.projectId}
            selectedValue={selectedField.value.path}
            readOnly={false}
            onValueSelected={onNodeChanged}
            showSelectedDate={true}
            visibleItemCount={this._visibleItemCount}
            updateMruOnSelection={false}
            tree={root.node} />;
    }

    private _resolveClassificationPicker = (classificationPicker: MruClassificationPicker) => {
        this._classificationPicker = classificationPicker;
    }

    private _onAreaPathNodeChanged = (value: string, node?: INode) => {
        const validationResult: IValidationResult = NewTestPlanFormValidators.validateNodeValue(value, node);
        this.props.areaPathNodeSelected(value);
        this.setState({
            selectedAreaPathField: {
                value: {
                    path: value,
                    node: node
                },
                validationResult,
                changed: true
            }
        });
    }

    private _onIterationNodeChanged = (value: string, node?: INode) => {
        const validationResult: IValidationResult = NewTestPlanFormValidators.validateNodeValue(value, node);
        this.props.iterationNodeSelected(value);
        this.setState({
            selectedIterationField: {
                value: {
                    path: value,
                    node: node
                },
                validationResult,
                changed: true
            }
        });
    }
}

registerLWPComponent("area-path-and-iteration-picker", AreaPathAndIterationPicker);
